import { useEffect, useRef, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Toolbar } from 'primereact/toolbar';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { Checkbox } from 'primereact/checkbox';
import { LettersTab } from '../../LettersTab';
import { CancellationTab } from '../CancellationLetterTab';
import Loader from '../../../../components/Loadeer/Loader';
import { getAllDetailsDirect, getSpecificDetailsById, postAllData } from '../../../../service/ProductService';
import { CANCELLATIONLETTER, DOWNLOADCANCELLATIONLETTER, STORECANCELLATIONLETTER } from '../../../../config/ApiConstant';
import { convertDateFrom, defaultPageRowOptions, defaultRowOptions, SkeletonbodyTemplate, Skeletonitems } from '../../../../appconfig/Settings';
import { SubmissionLetterListColumns } from '../../../../appconfig/DatatableSetting';
import { amountTemplate, dateTemplate } from '../../../../appconfig/DatatableTemplateSetting';
import { LetterShowClick, validateSubmissionLetterForm } from '../../../../config/Validate';

export const SubmissionLetter = () => {
    document.title = 'Submission Letter | GSPC';

    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [pageLoad, setPageLoad] = useState<boolean>(true);
    const [errors, setErrors] = useState<any>({});
    const [regNo, setRegNo] = useState<string>('');
    const [submissionLetterData, setSubmissionLetterData] = useState<any>({
        collegeChecked: false,
        practicalChecked: false,
        nocChecked: false,
        schoolLetterDate: new Date(),
        schoolReplyDate: new Date(),
        boardLetterDate: new Date(),
        boardReplyDate: new Date(),
        collegeLetterDate: new Date(),
        collegeReplyDate: new Date(),
        universityLetterDate: new Date(),
        universityReplyDate: new Date(),
        nocLetterDate: new Date(),
        nocReplyDate: new Date(),
        practicalLetterDate: new Date(),
        practicalReplyDate: new Date(),
        council_name: ''
    });
    const [addLoader, setAddLoader] = useState<boolean>(false);
    const [submissionLetterList, setSubmissionLetterList] = useState<any[]>([]);
    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [buttonVisible, setButtonVisible] = useState<boolean>(true);

    useEffect(() => {
        getSubmissionLetter();
    }, []);

    const leftToolbarTemplate = () => (
        <span className="search-bar mr-3">
            <i className="pi pi-search"></i>
            <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
        </span>
    );

    const rightToolbarTemplate = () => <Button icon="pi pi-filter-slash" label="Clear" onClick={() => clearAllFilter()} className="p-button-outlined" />;

    const clearAllFilter = () => {
        setGlobalFieldFilter(null);
        setGlobalFilter(null);
    };

    const clearAll = () => {
        setRegNo('');
        setSubmissionLetterData({
            collegeChecked: false,
            practicalChecked: false,
            nocChecked: false,
            schoolLetterDate: new Date(),
            schoolReplyDate: new Date(),
            boardLetterDate: new Date(),
            boardReplyDate: new Date(),
            collegeLetterDate: new Date(),
            collegeReplyDate: new Date(),
            universityLetterDate: new Date(),
            universityReplyDate: new Date(),
            nocLetterDate: new Date(),
            nocReplyDate: new Date(),
            practicalLetterDate: new Date(),
            practicalReplyDate: new Date(),
            RegNo: '',
        });
        setErrors({});
    };

    const getSubmissionLetter = async () => {
        try {
            setPageLoad(false);
            setShowLoading(true);
            const response = await getAllDetailsDirect(CANCELLATIONLETTER + '?cancelby=Submission&search=');
            if (response) {
                const { List } = response;
                setSubmissionLetterList(List || []);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load data' });
        } finally {
            setShowLoading(false);
            setPageLoad(true);
        }
    };

    const getUserDetails = async () => {
        try {
            const { errors, isError } = LetterShowClick(regNo);

            setErrors(errors);
            if (!isError) {
                setSearchLoading(true);
                const response = await getAllDetailsDirect(CANCELLATIONLETTER + '?cancelby=Submission&FileNo=' + regNo);
                if (response && response.Details) {
                    const { StudF, StudM, StudL, feePaidApplicationDate, RegNo, postAddress, postDist, postPIN, PPhone } = response.Details;
                    let address =
                        (!window.cn(postAddress) ? postAddress : "") +
                        ", " +
                        (!window.cn(postDist) ? postDist : "") +
                        "-" +
                        (!window.cn(postPIN) ? postPIN : "") +
                        ", " +
                        (!window.cn(PPhone) ? "Mobile-" + PPhone : "");

                    let FromNm = `${StudL || ''} ${StudF || ''} ${StudM || ''}`.trim();
                    setSubmissionLetterData({
                        ...submissionLetterData,
                        Name: FromNm,
                        RegNo: RegNo,
                        ToAddress: address,
                        feePaidApplicationDate: convertDateFrom(feePaidApplicationDate)
                    });
                    setButtonVisible(false);
                } else {
                    setButtonVisible(true);
                    setSubmissionLetterData({ ...submissionLetterData });
                }
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not load data' });
        } finally {
            setSearchLoading(false);
        }
    };

    const onInputChange = (e: any, name: string) => {
        const val = e?.target?.value ?? e?.value ?? e;
        setSubmissionLetterData((prev: any) => ({ ...prev, [name]: val }));
    };

    // Handle Submission Letter Submit
    const addSubmissionLetterData = async () => {
        try {
            const { errors, isError } = validateSubmissionLetterForm(submissionLetterData);

            setErrors(errors);
            if (isError) return;


            const submission_responses: any = {};

            if (submissionLetterData.schoolChecked) {
                submission_responses.school = {
                    letter_date: convertDateFrom(submissionLetterData.schoolLetterDate),
                    reply_received_date: convertDateFrom(submissionLetterData.schoolReplyDate)
                };
            }

            if (submissionLetterData.boardChecked) {
                submission_responses.board = {
                    letter_date: convertDateFrom(submissionLetterData.boardLetterDate),
                    reply_received_date: convertDateFrom(submissionLetterData.boardReplyDate)
                };
            }

            if (submissionLetterData.collegeChecked) {
                submission_responses.college = {
                    letter_date: convertDateFrom(submissionLetterData.collegeLetterDate),
                    reply_received_date: convertDateFrom(submissionLetterData.collegeReplyDate),
                    serial_number: submissionLetterData.collegeSerialNo
                };
            }

            if (submissionLetterData.universityChecked) {
                submission_responses.university = {
                    letter_date: convertDateFrom(submissionLetterData.universityLetterDate),
                    reply_received_date: convertDateFrom(submissionLetterData.universityReplyDate)
                };
            }

            if (submissionLetterData.nocChecked) {
                submission_responses.noc = {
                    letter_date: convertDateFrom(submissionLetterData.nocLetterDate),
                    reply_received_date: convertDateFrom(submissionLetterData.nocReplyDate),
                    council_name: submissionLetterData.council_name
                };
            }

            if (submissionLetterData.practicalChecked) {
                submission_responses.practical_training = {
                    letter_date: convertDateFrom(submissionLetterData.practicalLetterDate),
                    reply_received_date: convertDateFrom(submissionLetterData.practicalReplyDate)
                };
            }

            const requestData: any = {
                FileNo: regNo,
                RegNo: submissionLetterData.RegNo || '',
                Name: submissionLetterData.Name || '',
                cancelby: 'Submission',
                ToAddress: submissionLetterData.ToAddress || '',
                feePaidApplicationDate: convertDateFrom(submissionLetterData?.feePaidApplicationDate)
            };

            if (Object.keys(submission_responses).length > 0) {
                requestData.submission_responses = JSON.stringify(submission_responses);
            }

            setAddLoader(true);
            const response = await postAllData(STORECANCELLATIONLETTER, requestData);

            if (response) {
                toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Submission Letter saved successfully' });
                clearAll();
                getUserDetails(); // this will re-fill the form data
                getSubmissionLetter();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong while saving' });
        } finally {
            setAddLoader(false);
        }
    };

    const actionsTemplate = (rowData: any) => (
        <div className="actions action-btn">
            <Button icon="pi pi-download" onClick={() => downloadSubmissionLetter(rowData?.SrNo)} tooltip="Download" tooltipOptions={{ position: 'top' }} />
        </div>
    );

    const downloadSubmissionLetter = async (id: any) => {
        try {
            setLoading(true);
            const response = await getSpecificDetailsById(DOWNLOADCANCELLATIONLETTER, id);
            if (response?.file_url) {
                const fileName = `submission-letter-${new Date().getTime()}.pdf`;
                const link = document.createElement('a');
                link.href = response.file_url;
                link.target = '_blank';
                link.download = fileName;
                document.body.appendChild(link);
                link.click();
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Download failed' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <LettersTab mainTabLabel="Cancellation Letters" />
            <CancellationTab initialTabLabel="Submission Letter" />
            <Toast ref={toast} />

            <div className="grid crud-demo mt-2">
                <div className="col-12">
                    <div className="card">
                        <div className="p-fluid formgrid grid align-items-center">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="regNo">
                                    File No <span style={{ color: 'red' }}>*</span>
                                </label>
                                <InputText value={regNo} name="regNo" keyfilter="num" autoComplete="off" placeholder="Enter File No." onChange={(e) => setRegNo(e.target.value)} className={errors['regNo'] && 'p-invalid'} />
                                <small className="p-invalid-txt">{errors['regNo']}</small>
                            </div>
                            <div className="field col-12 md:col-1 mt-4">
                                <Button label="Search" className="p-button-primary" onClick={() => getUserDetails()} loading={searchLoading} />
                            </div>
                        </div>

                        <hr />

                        <div className="p-fluid formgrid grid">
                            <div className="field col-12 md:col-4">
                                <label htmlFor="Name">Name</label>
                                <InputText value={submissionLetterData?.Name || ''} name="Name" disabled />
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="RefranceLetDate">Fee Paid Application Date <span style={{ color: 'red' }}>*</span></label>
                                <Calendar
                                    value={
                                        !window.cn(submissionLetterData?.feePaidApplicationDate)
                                            ? new Date(submissionLetterData.feePaidApplicationDate)
                                            : null
                                    }
                                    disabled
                                    dateFormat="dd-mm-yy"
                                />
                            </div>

                            <div className="field col-12 md:col-4">
                                <label htmlFor="ToAddress">Address</label>
                                <InputTextarea value={submissionLetterData?.ToAddress || ''} rows={4} onChange={(e) => onInputChange(e, 'ToAddress')} />
                            </div>
                        </div>

                        {/* Letters On Section */}
                        <div className="p-fluid formgrid grid">
                            <h4 className="py-3 pl-2 m-0">Letters On*</h4>

                            {/* School */}
                            <div className="field col-12 md:col-12">
                                <div className="formgrid grid align-items-center">
                                    <div className="field-checkbox col-12 md:col-2">
                                        <Checkbox inputId="school" checked={submissionLetterData?.schoolChecked} onChange={(e) => onInputChange({ value: e.checked }, 'schoolChecked')} />
                                        <label htmlFor="school">School</label>
                                    </div>

                                    {submissionLetterData?.schoolChecked && (
                                        <>
                                            <div className="field col-12 md:col-4">
                                                <label>Letter Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.schoolLetterDate} onChange={(e: any) => onInputChange(e.value, 'schoolLetterDate')} showIcon className={errors['schoolLetterDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['schoolLetterDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-4">
                                                <label>Reply Received Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.schoolReplyDate} onChange={(e: any) => onInputChange(e.value, 'schoolReplyDate')} showIcon className={errors['schoolReplyDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['schoolReplyDate']}</small>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Board */}
                            <div className="field col-12 md:col-12">
                                <div className="formgrid grid align-items-center">
                                    <div className="field-checkbox col-12 md:col-2">
                                        <Checkbox inputId="board" checked={submissionLetterData?.boardChecked} onChange={(e) => onInputChange({ value: e.checked }, 'boardChecked')} />
                                        <label htmlFor="board">Board</label>
                                    </div>

                                    {submissionLetterData?.boardChecked && (
                                        <>
                                            <div className="field col-12 md:col-4">
                                                <label>Letter Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.boardLetterDate} onChange={(e: any) => onInputChange(e.value, 'boardLetterDate')} showIcon className={errors['boardLetterDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['boardLetterDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-4">
                                                <label>Reply Received Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.boardReplyDate} onChange={(e: any) => onInputChange(e.value, 'boardReplyDate')} showIcon className={errors['boardReplyDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['boardReplyDate']}</small>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* College */}
                            <div className="field col-12 md:col-12">
                                <div className="formgrid grid align-items-center">
                                    <div className="field-checkbox col-12 md:col-2">
                                        <Checkbox inputId="college" checked={submissionLetterData?.collegeChecked} onChange={(e) => onInputChange({ value: e.checked }, 'collegeChecked')} />
                                        <label htmlFor="college">College</label>
                                    </div>

                                    {submissionLetterData?.collegeChecked && (
                                        <>
                                            <div className="field col-12 md:col-4">
                                                <label>Letter Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.collegeLetterDate} onChange={(e: any) => onInputChange(e.value, 'collegeLetterDate')} showIcon className={errors['collegeLetterDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['collegeLetterDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-4">
                                                <label>Reply Received Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.collegeReplyDate} onChange={(e: any) => onInputChange(e.value, 'collegeReplyDate')} showIcon className={errors['collegeReplyDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['collegeReplyDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-2">
                                                <label>Serial Number <span style={{ color: 'red' }}>*</span></label>
                                                <InputText
                                                    value={submissionLetterData?.collegeSerialNo || ''}
                                                    onChange={(e) => onInputChange(e, 'collegeSerialNo')}
                                                    className={errors['collegeSerialNo'] && 'p-invalid'}
                                                />
                                                <small className="p-invalid-txt">{errors['collegeSerialNo']}</small>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* University */}
                            <div className="field col-12 md:col-12">
                                <div className="formgrid grid align-items-center">
                                    <div className="field-checkbox col-12 md:col-2">
                                        <Checkbox inputId="university" checked={submissionLetterData?.universityChecked} onChange={(e) => onInputChange({ value: e.checked }, 'universityChecked')} />
                                        <label htmlFor="university">University</label>
                                    </div>

                                    {submissionLetterData?.universityChecked && (
                                        <>
                                            <div className="field col-12 md:col-4">
                                                <label>Letter Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.universityLetterDate} onChange={(e: any) => onInputChange(e.value, 'universityLetterDate')} showIcon className={errors['universityLetterDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['universityLetterDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-4">
                                                <label>Reply Received Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.universityReplyDate} onChange={(e: any) => onInputChange(e.value, 'universityReplyDate')} showIcon className={errors['universityReplyDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['universityReplyDate']}</small>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* NOC */}
                            <div className="field col-12 md:col-12">
                                <div className="formgrid grid align-items-center">
                                    <div className="field-checkbox col-12 md:col-2">
                                        <Checkbox inputId="noc" checked={submissionLetterData?.nocChecked} onChange={(e) => onInputChange({ value: e.checked }, 'nocChecked')} />
                                        <label htmlFor="noc">NOC</label>
                                    </div>

                                    {submissionLetterData?.nocChecked && (
                                        <>
                                            <div className="field col-12 md:col-4">
                                                <label>Letter Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.nocLetterDate} onChange={(e: any) => onInputChange(e.value, 'nocLetterDate')} showIcon className={errors['nocLetterDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['nocLetterDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-4">
                                                <label>Reply Received Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.nocReplyDate} onChange={(e: any) => onInputChange(e.value, 'nocReplyDate')} showIcon className={errors['nocReplyDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['nocReplyDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-2">
                                                <label>Council <span style={{ color: 'red' }}>*</span></label>
                                                <InputText
                                                    value={submissionLetterData?.council_name || ''}
                                                    onChange={(e) => onInputChange(e, 'council_name')}
                                                    className={errors['council_name'] && 'p-invalid'}
                                                />
                                                <small className="p-invalid-txt">{errors['council_name']}</small>
                                            </div>
                                        </>
                                    )}
                                </div>
                                {errors.selectedLettersOn && (
                                    <small className="p-invalid-txt">{errors.selectedLettersOn}</small>
                                )}
                            </div>

                            {/* Practical */}
                            <div className="field col-12 md:col-12">
                                <div className="formgrid grid align-items-center">
                                    <div className="field-checkbox col-12 md:col-2">
                                        <Checkbox inputId="practical" checked={submissionLetterData?.practicalChecked} onChange={(e) => onInputChange({ value: e.checked }, 'practicalChecked')} />
                                        <label htmlFor="practical">Practical Training</label>
                                    </div>

                                    {submissionLetterData?.practicalChecked && (
                                        <>
                                            <div className="field col-12 md:col-4">
                                                <label>Letter Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.practicalLetterDate} onChange={(e: any) => onInputChange(e.value, 'practicalLetterDate')} showIcon className={errors['practicalLetterDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['practicalLetterDate']}</small>
                                            </div>

                                            <div className="field col-12 md:col-4">
                                                <label>Reply Received Date <span style={{ color: 'red' }}>*</span></label>
                                                <Calendar dateFormat="dd-mm-yy" value={submissionLetterData?.practicalReplyDate} onChange={(e: any) => onInputChange(e.value, 'practicalReplyDate')} showIcon className={errors['practicalReplyDate'] && 'p-invalid'} />
                                                <small className="p-invalid-txt">{errors['practicalReplyDate']}</small>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className="formgrid grid">
                            <div className="field col-12 md:col-12" style={{ display: 'flex', justifyContent: 'end', gap: 8 }}>
                                <Button label="Clear" icon="pi pi-times" className="p-button-secondary" onClick={clearAll} />
                                <Button label="Save" icon="pi pi-check" onClick={addSubmissionLetterData} loading={addLoader} disabled={buttonVisible} />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>
                        {pageLoad ? (
                            <DataTable
                                value={submissionLetterList}
                                showGridlines
                                stripedRows
                                globalFilter={globalFilter}
                                filters={globalFieldFilter}
                                onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                rows={defaultRowOptions}
                                paginator={submissionLetterList.length >= 10}
                                rowsPerPageOptions={defaultPageRowOptions}
                                className="datatable-responsive p-datatable-sm"
                                emptyMessage="No Data Found"
                                removableSort
                            >
                                {SubmissionLetterListColumns.map((col) => {
                                    if (col.field === 'action') return <Column key={col.field} field={col.field} header={col.header} body={actionsTemplate} />;
                                    if (col.field === 'created_at') return <Column key={col.field} field={col.field} header={col.header} body={dateTemplate} />;
                                    if (col.field === 'amount') return <Column key={col.field} field={col.field} header={col.header} body={amountTemplate} />;
                                    return <Column key={col.field} field={col.field} header={col.header} sortable filter filterPlaceholder={'Search by ' + col.header} />;
                                })}
                            </DataTable>
                        ) : (
                            <DataTable value={Skeletonitems}>
                                {SubmissionLetterListColumns.map((col) => (
                                    <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                ))}
                            </DataTable>
                        )}
                    </div>
                </div>
            </div>

            {loading === true && <Loader />}
        </>
    );
};
